import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  Maximize2,
  MessageSquareQuote,
  X,
} from "lucide-react";
import Reveal from "@/components/Reveal";

const FEEDBACKS = [
  {
    src: "https://customer-assets-7cd3h4nn.emergentagent.net/job_rebate-lot-hadiah/artifacts/912f6397492a734d_6240072596573590513.webp",
    alt: "Feedback klien Mohamad Gus — rebate pertama US$18 dan 1,5 lot tercatat di kemitraan",
  },
  {
    src: "https://customer-assets-7cd3h4nn.emergentagent.net/job_rebate-lot-hadiah/artifacts/018ea3b5fbf5f4d3_6240072596573590512.webp",
    alt: "Feedback klien Deki Tecnikal — total rebate US$186,12 dengan 15,51 lot",
  },
  {
    src: "https://customer-assets-7cd3h4nn.emergentagent.net/job_rebate-lot-hadiah/artifacts/0433d94105f2317d_6240072596573590511.webp",
    alt: "Feedback klien Arif Budiman — tautan referral dan halaman kemitraan",
  },
  {
    src: "https://customer-assets-7cd3h4nn.emergentagent.net/job_rebate-lot-hadiah/artifacts/68de4701e187d7c7_6240072596573590510.webp",
    alt: "Feedback klien Arif Budiman — penarikan rebate Rp2.954.093 ke bank berhasil",
  },
];

export default function Testimonials() {
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(null);
  const [zoomed, setZoomed] = useState(false);

  const stepOf = (track) =>
    track.children.length > 1
      ? track.children[1].offsetLeft - track.children[0].offsetLeft
      : 0;

  const onScroll = () => {
    const track = trackRef.current;
    if (!track) return;
    const step = stepOf(track);
    if (step > 0) setActive(Math.min(FEEDBACKS.length - 1, Math.round(track.scrollLeft / step)));
  };

  const scrollToCard = useCallback((i) => {
    const track = trackRef.current;
    if (!track) return;
    const idx = Math.max(0, Math.min(FEEDBACKS.length - 1, i));
    track.scrollTo({ left: idx * stepOf(track), behavior: "smooth" });
  }, []);

  const openLightbox = (i) => {
    setLightbox(i);
    setZoomed(false);
    window.__lenis?.stop();
  };

  const closeLightbox = useCallback(() => {
    setLightbox(null);
    setZoomed(false);
    window.__lenis?.start();
  }, []);

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") setLightbox((v) => (v === null ? v : (v + 1) % FEEDBACKS.length));
      if (e.key === "ArrowLeft") setLightbox((v) => (v === null ? v : (v - 1 + FEEDBACKS.length) % FEEDBACKS.length));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, closeLightbox]);

  return (
    <section id="testimoni" className="relative overflow-hidden py-20 sm:py-28">
      <div className="pointer-events-none absolute left-0 top-1/3 h-[380px] w-[380px] rounded-full bg-[#00F2B6]/8 blur-[130px]" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
        <Reveal className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <p className="flex items-center gap-2 font-mono text-[11px] font-bold uppercase tracking-[0.3em] text-[#00F2B6] sm:text-sm">
              <MessageSquareQuote className="h-4 w-4" />
              Real Feedback
            </p>
            <h2
              data-testid="testimonials-title"
              className="mt-3 font-display text-3xl font-black uppercase tracking-tight text-white sm:text-5xl"
            >
              Feedback Nyata{" "}
              <span className="text-gradient-gold">Dari Klien Kami</span>
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-zinc-400 sm:text-base">
              Simak pengalaman yang dibagikan langsung oleh klien kami. Geser
              untuk melihat lebih banyak testimoni.
            </p>
            <p className="mt-2 flex items-center gap-1.5 font-mono text-[11px] text-zinc-500">
              <Maximize2 className="h-3.5 w-3.5 text-[#F0B429]" />
              Ketuk gambar untuk memperbesar.
            </p>
          </div>
          <div className="hidden gap-3 md:flex">
            <button
              data-testid="testimonial-prev-button"
              aria-label="Testimoni sebelumnya"
              onClick={() => scrollToCard(active - 1)}
              className="glass flex h-12 w-12 items-center justify-center rounded-full text-white transition-all hover:border-[#F0B429]/50 hover:text-[#F0B429] disabled:opacity-30"
              disabled={active === 0}
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              data-testid="testimonial-next-button"
              aria-label="Testimoni berikutnya"
              onClick={() => scrollToCard(active + 1)}
              className="glass flex h-12 w-12 items-center justify-center rounded-full text-white transition-all hover:border-[#F0B429]/50 hover:text-[#F0B429] disabled:opacity-30"
              disabled={active === FEEDBACKS.length - 1}
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div
            ref={trackRef}
            onScroll={onScroll}
            data-lenis-prevent
            data-testid="testimonials-track"
            className="no-scrollbar mt-10 flex snap-x snap-mandatory gap-5 overflow-x-auto pb-2 sm:gap-6"
          >
            {FEEDBACKS.map((f, i) => (
              <figure
                key={i}
                data-testid={`testimonial-card-${i}`}
                className="group w-[85%] shrink-0 snap-start rounded-3xl border border-white/10 bg-[#0D111A] p-3 transition-all duration-300 hover:border-[#F0B429]/40 hover:shadow-[0_20px_50px_rgba(240,180,41,0.12)] sm:w-[calc((100%-20px)/2)] lg:w-[calc((100%-48px)/3)]"
              >
                <button
                  data-testid={`testimonial-image-${i}`}
                  onClick={() => openLightbox(i)}
                  aria-label={`Perbesar feedback klien ${i + 1}`}
                  className="block w-full cursor-zoom-in overflow-hidden rounded-2xl"
                >
                  <img
                    src={f.src}
                    alt={f.alt}
                    loading="lazy"
                    className="h-auto w-full transition-transform duration-500 group-hover:scale-[1.015]"
                  />
                </button>
                <figcaption className="flex items-center justify-between px-2 pb-1 pt-3">
                  <span className="font-mono text-[11px] font-bold tracking-[0.2em] text-zinc-500">
                    FEEDBACK KLIEN
                  </span>
                  <span className="font-mono text-[11px] font-bold text-[#F0B429]">
                    0{i + 1}
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </Reveal>

        <div className="mt-6 flex items-center justify-center gap-2">
          {FEEDBACKS.map((_, i) => (
            <button
              key={i}
              data-testid={`testimonial-dot-${i}`}
              aria-label={`Ke testimoni ${i + 1}`}
              onClick={() => scrollToCard(i)}
              className="flex h-6 items-center justify-center px-1"
            >
              <span
                className={`h-2 rounded-full transition-all duration-300 ${
                  active === i ? "w-7 bg-[#F0B429]" : "w-2 bg-zinc-600"
                }`}
              />
            </button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {lightbox !== null && (
          <motion.div
            data-testid="testimonial-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-[#07090E]/95 backdrop-blur-xl"
            onClick={closeLightbox}
          >
            <button
              data-testid="lightbox-close-button"
              aria-label="Tutup"
              onClick={closeLightbox}
              className="glass absolute right-4 top-4 z-10 flex h-11 w-11 items-center justify-center rounded-full text-white hover:border-[#FF2A3B]/60"
            >
              <X className="h-5 w-5" />
            </button>
            <span className="glass absolute left-4 top-4 z-10 rounded-full px-4 py-1.5 font-mono text-xs font-bold text-[#00F2B6]">
              {lightbox + 1} / {FEEDBACKS.length}
            </span>
            <button
              data-testid="lightbox-prev-button"
              aria-label="Sebelumnya"
              onClick={(e) => {
                e.stopPropagation();
                setLightbox((lightbox - 1 + FEEDBACKS.length) % FEEDBACKS.length);
                setZoomed(false);
              }}
              className="glass absolute left-3 z-10 flex h-12 w-12 items-center justify-center rounded-full text-white hover:border-[#F0B429]/50 sm:left-6"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button
              data-testid="lightbox-next-button"
              aria-label="Berikutnya"
              onClick={(e) => {
                e.stopPropagation();
                setLightbox((lightbox + 1) % FEEDBACKS.length);
                setZoomed(false);
              }}
              className="glass absolute right-3 z-10 flex h-12 w-12 items-center justify-center rounded-full text-white hover:border-[#F0B429]/50 sm:right-6"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
            <motion.div
              key={lightbox}
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="max-h-[88vh] max-w-[92vw] overflow-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                data-testid="lightbox-image"
                src={FEEDBACKS[lightbox].src}
                alt={FEEDBACKS[lightbox].alt}
                onClick={() => setZoomed(!zoomed)}
                className={`mx-auto h-auto rounded-2xl border border-white/15 transition-all duration-300 ${
                  zoomed
                    ? "w-[165%] max-w-none cursor-zoom-out"
                    : "w-auto max-h-[88vh] max-w-full cursor-zoom-in"
                }`}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
